/**
 * SummaryStatCard — A2F-488
 *
 * Single-number summary card used in the Review pages' non-filtering
 * branch (no submission focus active). Replaces the per-page local
 * `SummaryCard` definitions that existed since before A2F-487.
 *
 * When `statusValue` + `onFilter` are provided, the whole card becomes
 * a filter shortcut button. Clicking sets the status filter only (scope
 * stays `all` — there is no scope breakdown without submission focus).
 *
 * `Files Scanned` cards and other scan-stats metrics pass no
 * `onFilter`, so they render as plain text cards — not every card is
 * a valid filter target.
 */

type ColorKey = 'slate' | 'red' | 'orange' | 'blue' | 'green';

const BIG_NUMBER_COLOR: Record<ColorKey, string> = {
  slate: 'text-slate-700',
  red: 'text-red-600',
  orange: 'text-orange-600',
  blue: 'text-blue-600',
  green: 'text-green-600',
};

export interface SummaryStatCardProps {
  label: string;
  value: number;
  color: ColorKey;

  /**
   * A2F-488: when provided with `onFilter`, the whole card becomes a
   * click shortcut that sets the status filter.
   */
  statusValue?: string;
  activeStatus?: string;
  onFilter?: (status: string) => void;
}

export function SummaryStatCard({
  label,
  value,
  color,
  statusValue,
  activeStatus,
  onFilter,
}: SummaryStatCardProps) {
  const clickable = onFilter != null && statusValue != null;
  const isActive = clickable && activeStatus === statusValue;

  const content = (
    <>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${BIG_NUMBER_COLOR[color]}`}>{value}</p>
    </>
  );

  if (!clickable) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col justify-between">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onFilter!(statusValue!)}
      aria-pressed={isActive}
      title={`Show ${label.toLowerCase()}`}
      className={`text-left bg-white rounded-xl border p-4 flex flex-col justify-between transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 ${
        isActive ? 'border-brand-orange ring-2 ring-brand-orange/40 bg-orange-50' : 'border-slate-200'
      }`}
    >
      {content}
    </button>
  );
}
